package com.essntl.features.file.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.essntl.core.unsplash.ui.UnsplashImage
import com.essntl.features.file.ui.essntlgallery.EssntlGalleryEvent
import com.essntl.features.file.ui.essntlgallery.EssntlGalleryState
import com.essntl.features.file.ui.essntlgallery.rememberAllImagesSorted
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.scroll.EsVerticalScrollbar
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.toggle.EsTab
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun GalleryGrid(
    state: EssntlGalleryState,
    onEvent: (EssntlGalleryEvent) -> Unit,
    selectedImageIds: Set<String> = emptySet(),
    onSelect: (Boolean, String) -> Unit = { _, _ -> },
    onClick: (Boolean, String) -> Unit = { _, _ -> },
    onDelete: (String) -> Unit = { _ -> },
    onSwitchTab: () -> Unit = {},
    imageSize: DpSize? = null,
    deletable: Boolean = true,
    selectable: Boolean = true,
    modifier: Modifier = Modifier,
) {
    var searchText by remember { mutableStateOf("") }

    val images = state.rememberAllImagesSorted(searchText)

    val gridState = rememberLazyGridState()

    Column(
        horizontalAlignment = Alignment.Start,
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 2.5.spacing),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth(),
        ) {
            EsTab(
                items = state.tabItems,
                tabWidth = 160.dp,
                selectedItemIndex = state.selectedTabIndex,
                onClick = { tabState ->
                    onEvent(EssntlGalleryEvent.SwitchTab(tabState.index))
                    onSwitchTab()
                },
                modifier = Modifier,
            )

            Box(
                modifier = Modifier
                    .weight(1f)
                    .padding(horizontal = 16.dp),
                contentAlignment = Alignment.TopStart,
            ) {
                EsInput(
                    value = searchText,
                    onValueChange = { searchText = it },
                    prefix = {
                        EsLargeIcon(
                            res = EsIcons.Search,
                        )
                    },
                    placeholder = "Search",
                    modifier = Modifier.width(300.dp),
                )
            }
        }

        Spacer(Modifier.height(height = 3.5.spacing))

        if (state.getAllImages.isLoading)
            Box(
                contentAlignment = Alignment.Center,
                modifier = modifier,
            ) {
                EsSpinner(
                    color = MaterialTheme.esColorScheme.iconPrimary,
                    size = EsSpinnerSize.Large,
                    modifier = Modifier.align(Alignment.Center),
                )
            }
        else if (state.getAllImages.isFailure)
            Box(
                contentAlignment = Alignment.Center,
                modifier = modifier,
            ) {
                Text(
                    text = "Something goes wrong!",
                    textAlign = TextAlign.Center,
                    style = MaterialTheme.esTypography.p1Regular16,
                    color = MaterialTheme.esColorScheme.textPrimary,
                    modifier = Modifier.align(Alignment.Center),
                )
            }
        else if (images.isEmpty())
            Box(
                contentAlignment = Alignment.Center,
                modifier = modifier,
            ) {
                Text(
                    text = "No images found!",
                    textAlign = TextAlign.Center,
                    style = MaterialTheme.esTypography.p1Regular16,
                    color = MaterialTheme.esColorScheme.textPrimary,
                    modifier = Modifier.align(Alignment.Center),
                )
            }
        else
            Row(
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                LazyVerticalGrid(
                    columns = GridCells.Fixed(5),
                    state = gridState,
                    horizontalArrangement = Arrangement.spacedBy(1.spacing),
                    verticalArrangement = Arrangement.spacedBy(1.5.spacing),
                    userScrollEnabled = true,
                    modifier = Modifier.weight(1f),
                ) {
                    items(images) { image ->
                        Box(
                            modifier = Modifier
                                .aspectRatio(1f)
                                .fillMaxHeight()
                                .clip(shape = RoundedCornerShape(20.dp)),
                        ) {
                            val isSelected = remember(image.id, selectedImageIds) {
                                image.id in selectedImageIds
                            }

                            if (image.kmpFile != null) {
                                image.kmpFile?.let {
                                    UnsplashImage(
                                        model = it,
                                        selected = isSelected,
                                        imageSize = imageSize,
                                        onClick = { onClick(isSelected, image.id) },
                                        onSelect = { onSelect(isSelected, image.id) },
                                        onDelete = { onDelete(image.id) },
                                        deletable = deletable,
                                        selectable = selectable,
                                    )
                                }
                            } else {
                                UnsplashImage(
                                    url = image.url,
                                    selected = isSelected,
                                    imageSize = imageSize,
                                    onClick = { onClick(isSelected, image.id) },
                                    onSelect = { onSelect(isSelected, image.id) },
                                    onDelete = { onDelete(image.id) },
                                    deletable = deletable,
                                    selectable = selectable,
                                )
                            }
                        }
                    }
                }

                Spacer(Modifier.width(width = .5.spacing))

                EsVerticalScrollbar(
                    lazyGridState = gridState,
                )
            }
    }
}